import React, { useState, useEffect } from 'react';
import { List, Datagrid, DeleteButton, useNotify, useRefresh, useDataProvider } from 'react-admin';
import { Button, Typography } from '@mui/material';
import { baseUrl } from './consts';

const PopupAdList = () => {
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [popupAd, setPopupAd] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch the popup ad on component mount
    const fetchPopupAd = () => {
        fetch(baseUrl+'/v1/manager/popup-ad', {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            },
        })
        .then(response => response.json())
        .then(data => {
            // console.log(`data.has_image: ${data.has_image}`);
            if (data.has_image) {
                // Set the image URL from the response
                setPopupAd({ id: 1, file_name: data.file_name });
            } else {
                setPopupAd(null); // No image available
            }
        })
        .catch(() => {
            notify('Failed to fetch popup ad', { type: 'error' });
        })
        .finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        fetchPopupAd();
    }, [notify, refresh]);

    // Handle file selection with validation
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        // Validate file extension
        const allowedExtensions = /(\.png|\.jpg|\.jpeg)$/i;
        if (!allowedExtensions.exec(selectedFile.name)) {
            notify("Invalid file format. Only PNG, JPG, and JPEG are allowed", { type: 'warning' });
            setFile(null);
            return;
        }

        // Check image dimensions
        const img = new Image();
        img.onload = () => {
            const aspectRatio = img.height / img.width;
            const minRatio = 1.6; // Minimum ratio with 10% tolerance
            const maxRatio = 2.2; // Maximum ratio with 10% tolerance
            
            if (aspectRatio < minRatio || aspectRatio > maxRatio) {
                notify("Height must be 1.6 width of the image", { type: 'warning' });
                setFile(null);
            } else {
                setFile(selectedFile);
            }
        };
        img.src = URL.createObjectURL(selectedFile); 
    };

    // Handle file upload
    const handleUpload = () => {
        if (!file) return;
        setIsUploading(true);

        const formData = new FormData();
        formData.append('popupAd', file);

        fetch(baseUrl+'/v1/manager/popup-ad', {
            method: 'POST',
            body: formData,
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            },
        })
        .then(response => {
            if (!response.ok) throw new Error('Upload failed');
            return response.json();
        })
        .then(() => {
            notify("Popup Ad uploaded successfully", { type: 'info' });
            setPopupAd({ id: 1, file_name: URL.createObjectURL(file) });
            setFile(null);
            setIsUploading(false);
            refresh();
        })
        .catch(() => {
            notify("Failed to upload Popup Ad", { type: 'error' });
            setIsUploading(false);
        });
    };

    // Handle delete
    const handleDelete = () => {
        dataProvider.delete('popup-ad', { id: 1 })
            .then(() => {
                notify("Popup Ad deleted successfully", { type: 'info' });
                setPopupAd(null);
                refresh();
            })
            .catch(() => {
                notify("Failed to delete Popup Ad", { type: 'error' });
            });
    };

    return (
        <List title="Popup Ad">
            {loading ? (
                <Typography variant="body1">Loading...</Typography>
            ) : popupAd ? (
                <div style={{ padding: '10px', display: 'flex', alignItems: 'center' }}>
                    <img
                        src={popupAd.file_name}
                        alt="Popup Ad"
                        style={{
                            width: '100px',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                        }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = ''; 
                            notify('Failed to load image', { type: 'error' });
                        }}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleDelete}
                        style={{ marginLeft: '10px' }}
                    >
                        Delete
                    </Button>



                    
                </div>
            ) : (
                <>
                    <Typography variant="body1" style={{ marginBottom: '16px' }}>
                        No Popup Ad available.
                    </Typography>
                    <input
                        type="file"
                        accept=".png, .jpg, .jpeg"
                        onChange={handleFileChange}
                        style={{ marginTop: '16px', marginBottom: '8px' }}
                    />
                    <Button
                        onClick={handleUpload}
                        disabled={!file || isUploading}
                        variant="contained"
                        color="primary"
                    >
                        {isUploading ? 'Uploading...' : 'Upload Popup Ad'}
                    </Button>
                </>
            )}
        </List>
    );
};

export default PopupAdList;
